<template>
    <div class="pa-5">
        <v-card v-if="data">
            <v-card-title>
                <h1 class="title">{{ 'Actual Physicals Sources' | term }}</h1>
                <v-btn small color="secondary" class="ml-3" @click="create()">
                    <v-icon left>mdi-plus</v-icon>{{ $t('shared.button.add') }}
                </v-btn>
                <v-menu
                    v-model="importDialog"
                    max-width="310px"
                    :close-on-content-click="false"
                    v-if="!nonSystem.length"
                >
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" small class="ml-1" color="secondary">
                            <v-icon left>mdi-upload</v-icon>{{ $t('shared.button.import') }}
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-text>
                            <input ref="inputUpload" type="file" accept="text/csv" @change="getFile($event)" />
                        </v-card-text>
                        <v-card-actions>
                            <v-btn color="secondary" @click="importData">{{ $t('shared.button.upload') }}</v-btn>
                            <v-btn text @click="importDialog = false">{{ $t('shared.button.cancel') }}</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <v-btn
                    small
                    class="ml-1"
                    color="gray"
                    v-if="nonSystem.length"
                    @click="exportData()"
                >
                    <v-icon left>mdi-download</v-icon>{{ $t('shared.button.export') }}
                </v-btn>
                <v-btn small color="primary" class="ml-3" v-if="dirty" @click="saveList()">
                    <v-icon left>mdi-check</v-icon>{{ $t('shared.button.save') }}
                </v-btn>
                <v-btn small color="grey" class="ml-3" v-if="dirty" @click="loadData()">
                    <v-icon left>mdi-close</v-icon>{{ $t('shared.button.cancel') }}
                </v-btn>
                <v-spacer></v-spacer>
            </v-card-title>
            <standard-wrapping-reorderable
                :items="ordered"
                :item-id="'id'"
                :item-order="'priority'"
                @reordered="onItemsReordered"
                item-class="actual-physicals-source-type-item"
                handle-class="actual-physicals-source-type-handle"
            >
                <v-data-table disable-pagination hide-default-footer :headers="headers" :items="ordered">
                    <template v-slot:item="{ item }">
                        <tr :class="{ dirty: item.dirty, 'actual-physicals-source-type-item': true }" :key="`${item.id}-${item.dirty}`">
                            <td>
                                <div class="no-wrap">
                                    <v-icon class="actual-physicals-source-type-handle">mdi-drag</v-icon>
                                </div>
                            </td>
                            <td><v-icon v-if="isSystemSource(item)" class="mr-1" size="small">mdi-star-circle</v-icon>{{ item.name }}</td>
                            <td>
                                <div class="no-wrap">
                                    <v-btn text icon @click="edit(item)" v-if="!isSystemSource(item)">
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </standard-wrapping-reorderable>
        </v-card>
        <standard-dialog :render="!!selected" :value='showModal' :max-width="900" persistent>
            <template v-slot:title>
                <span v-if="selected.id">{{ $t('shared.button.edit') }}</span>
                <span v-else>{{ $t('shared.button.create') }}</span>
                <span class="ml-1">{{ 'Actual Physicals Source' | term }}</span>
            </template>
            <template v-slot:content>
                <v-form ref="form" :lazy-validation="true">
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" class="pa-2">
                                <v-text-field
                                    autocomplete="off"
                                    :label="$t('shared.label.name')"
                                    :rules="[$rules.required(), $rules.maxLength(100)]"
                                    v-model="selected.name"
                                    counter="100"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </template>
            <template v-slot:actions>
                <v-btn color='red' text v-if='selected && selected.id' @click='del()'>{{ $t('shared.button.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <v-btn color='primary' text @click='save()'>{{ $t('shared.button.save') }}</v-btn>
            </template>
            <template v-slot:offline-actions>
                <v-spacer></v-spacer>
                <v-btn color='gray' text @click='showModal = false'>{{ $t('shared.button.cancel') }}</v-btn>
                <offline-btn text></offline-btn>
            </template>
        </standard-dialog>
        <confirm ref="confirm"></confirm>
    </div>
</template>
<script>
import { EventBus, Events } from '@/lib/EventBus';
import { v4 as uuidv4 } from 'uuid';
import ReducedLevels from '@/lib/data/ReducedLevels';
import DownloadHelper from '@/lib/DownloadHelper';
import ActualPhysicalsSourceTypes from '@/lib/data/ActualPhysicalsSourceTypes';
import StandardDialog from '@/components/Shared/Common/StandardDialog.vue';
import StandardWrappingReorderable from '@/components/Shared/Common/StandardWrappingReorderable.vue';

export default {
    components: { StandardDialog, StandardWrappingReorderable },
    data() {
        return {
            data: null,
            selected: null,
            showModal: false,
            importDialog: false,
            file: null,
            headers: [
                { text: '', value: 'handle', width: '24px', align: 'left' },
                { text: 'Name', value: 'name' },
                { text: '', value: 'id', align: 'right' }
            ],
            reordered: false,
        };
    },
    async created() {
        await this.loadData();
    },
    destroyed() {},
    methods: {
        async loadData() {
            var d = await ActualPhysicalsSourceTypes.get();
            this.reordered = false;
            for (let x of d.sourceTypes) {
                x.dirty = false;
            }
            this.data = d.sourceTypes;
        },
        create() {
            var priority = this.ordered.length ? this.ordered[this.ordered.length - 1].priority + 1 : 1;
            this.edit({
                priority
            });
        },
        edit(item) {
            this.selected = item;
            this.showModal = true;
            setTimeout(() => this.$refs.form.resetValidation(), 1);
        },
        isSystemSource(sourceType) {
            return sourceType.isSystem;
        },
        async saveList() {
            const priorities = this.data.reduce((a,v)=>({...a, [v.id]: v.priority}), {});

            await ActualPhysicalsSourceTypes.reprioritise({
                _type: 'ReprioritiseActualPhysicalsSourceTypesCommand',
                priorities
            });

            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Actual Physicals Sources')} Saved`);
            await this.loadData();
        },
        async save() {
            if (!this.$refs.form.validate()
            || this.$wait.any) {
                return;
            }

            this.$wait.start('saving');
            try{
                if(this.selected.id){
                    await ActualPhysicalsSourceTypes.update({
                        _type: 'UpdateActualPhysicalsSourceTypeCommand',
                        id: this.selected.id,
                        name: this.selected.name,
                        priority: this.selected.priority
                    });
                } else{
                    await ActualPhysicalsSourceTypes.create({
                        _type: 'CreateActualPhysicalsSourceTypeCommand',
                        id: uuidv4(),
                        name: this.selected.name,
                        priority: this.selected.priority
                    });
                }
            }finally {
                this.$wait.end('saving');
            }


            EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Actual Physicals Source')} Saved`);
            this.showModal = false;
            await this.loadData();
        },
        async del() {
            if (
                await this.$refs.confirm.openAsDeleteResource(this.$termSync('Actual Physicals Source'),  { })
            ) {
                await ActualPhysicalsSourceTypes.delete(this.selected.id);
                EventBus.$emit(Events.ToastSuccess, `${this.$termSync('Actual Physicals Source')} Deleted`);
                this.showModal = false;
                await this.loadData();
            }
        },
        async exportData() {
            const result = await ActualPhysicalsSourceTypes.export();
            var csv = result.csvFile;
            var blob = DownloadHelper.makeBlobFromFileString(csv);
            DownloadHelper.download('ActualPhysicalsSourceTypes.csv', blob);
        },
        async importData() {
            try {
                this.$wait.start('saving');
                this.uploadResult = null;
                await ActualPhysicalsSourceTypes.import(this.file);
                this.importDialog = false;
                await this.loadData();
            } finally {
                this.$wait.end('saving');
            }
        },
        async getFile(e) {
            this.file = e.target.files[0];
        },
        onItemsReordered(reorderedItems) {
            // Update priorities based on new order
            reorderedItems.forEach((item, index) => {
                const originalItem = this.data.find(x => x.id === item.id);
                if (originalItem && originalItem.priority !== index + 1) {
                    originalItem.priority = index + 1;
                    originalItem.dirty = true;
                    this.reordered = true;
                }
            });
        },
    },
    computed: {
        ordered() {
            if (!this.data) {
                return [];
            }
            return [... this.data]
                .sort((a, b) => a.priority - b.priority);
        },
        nonSystem(){
          return this.ordered.filter(st=>!st.isSystem)
        },
        dirty() {
            for (let x of this.data) {
                if (x.dirty) {
                    return true;
                }
            }
            return false;
        },
    }
};
</script>
